import { API } from "@/api/api";
import { useError } from "@/composables/useError";
import i18n from "@/plugins/i18n";
import { generateId } from "@/utils/generateId";
import type { Response } from "@/api/client";
import type { TravelOrderDto } from "@/api/trips/dto/trip.dto";
import type { SealDto } from "@/api/trips/dto/seal.dto";
import type { StopInfoSeal, StopInfoSealsSaveResult } from "@/types/trip";

const t = i18n.global.t;

export function useSeals() {
  const api = new API();
  const { showAPIError } = useError();

  function mapSealsToStopInfo(seals: SealDto[]): StopInfoSeal[] {
    return seals.map((seal) => {
      return {
        id: seal.id,
        tempId: generateId(),
        number: seal.number,
        intact: seal.intact,
        travelOrderId: seal.travelOrderId,
      };
    });
  }

  function getTravelOrdersSeals(travelOrders: TravelOrderDto[]): StopInfoSeal[] {
    return mapSealsToStopInfo(travelOrders.flatMap((travelOrder) => travelOrder.seals ?? []));
  }

  function applySealDto(seal: StopInfoSeal, sealDto: SealDto): StopInfoSeal {
    return {
      ...seal,
      id: sealDto.id,
      number: sealDto.number,
      intact: sealDto.intact,
    };
  }

  function getCreatedSeals(seals: StopInfoSeal[]): StopInfoSeal[] {
    return seals.filter((seal) => !seal.id);
  }

  function getUpdatedSeals(seals: StopInfoSeal[], initialSeals: StopInfoSeal[]): StopInfoSeal[] {
    return seals.filter((seal) => {
      const initialSeal = initialSeals.find((item) => item.id === seal.id);

      if (!seal.id || !initialSeal) {
        return false;
      }

      return initialSeal.number !== seal.number || initialSeal.intact !== seal.intact;
    });
  }

  function getDeletedSeals(seals: StopInfoSeal[], initialSeals: StopInfoSeal[]): StopInfoSeal[] {
    return initialSeals.filter((initialSeal) => {
      if (!initialSeal.id) {
        return false;
      }

      return !seals.some((seal) => seal.id === initialSeal.id);
    });
  }


  function getSavedSeals(seals: StopInfoSeal[], responses: Response<SealDto>[]): Map<string, SealDto> {
    const savedSeals = new Map<string, SealDto>();

    responses.forEach((response, index) => {
      if (response.success && response.data) {
        savedSeals.set(seals[index].tempId, response.data);
      }
    });

    return savedSeals;
  }

  async function saveSeals(
    tripId: string | number,
    seals: StopInfoSeal[],
    initialSeals: StopInfoSeal[],
  ): Promise<StopInfoSealsSaveResult> {
    const createdSeals = getCreatedSeals(seals);
    const updatedSeals = getUpdatedSeals(seals, initialSeals);
    const deletedSeals = getDeletedSeals(seals, initialSeals);

    if (!createdSeals.length && !updatedSeals.length && !deletedSeals.length) {
      return { isSaved: true, seals, initialSeals };
    }

    try {
      const [createResponses, updateResponses] = await Promise.all([
        Promise.all(
          createdSeals.map((seal) => {
            return api.seals.createSeal(tripId, seal.travelOrderId, { number: seal.number, intact: seal.intact });
          }),
        ),
        Promise.all(
          updatedSeals.map((seal) => {
            return api.seals.updateSeal(tripId, seal.travelOrderId, Number(seal.id), {
              number: seal.number,
              intact: seal.intact,
            });
          }),
        ),
      ]);

      const createdSealDtos = getSavedSeals(createdSeals, createResponses);
      const updatedSealDtos = getSavedSeals(updatedSeals, updateResponses);

      const nextSeals = seals.map((seal) => {
        const sealDto = createdSealDtos.get(seal.tempId) ?? updatedSealDtos.get(seal.tempId);

        return sealDto ? applySealDto(seal, sealDto) : seal;
      });

      const nextInitialSeals = [
        ...initialSeals.map((initialSeal) => {
          const updatedSeal = updatedSeals.find((seal) => seal.id === initialSeal.id);
          const sealDto = updatedSeal ? updatedSealDtos.get(updatedSeal.tempId) : undefined;

          return sealDto ? applySealDto(initialSeal, sealDto) : initialSeal;
        }),
        ...nextSeals.filter((seal) => createdSealDtos.has(seal.tempId)),
      ];

      const failedResponse = [...createResponses, ...updateResponses].find((response) => !response.success);

      if (failedResponse) {
        showAPIError(failedResponse, t("trip.seals-save-failed"));
      }

      return { isSaved: !failedResponse, seals: nextSeals, initialSeals: nextInitialSeals };
    } catch (error) {
      console.error(error);

      return { isSaved: false, seals, initialSeals };
    }
  }

  async function deleteSeal(tripId: string | number, seal: StopInfoSeal): Promise<boolean> {
    try {
      const response = await api.seals.deleteSeal(tripId, seal.travelOrderId, Number(seal.id));

      if (!response.success) {
        showAPIError(response, t("trip.seal-delete-failed"));

        return false;
      }

      return true;
    } catch (error) {
      console.error(error);

      return false;
    }
  }

  return {
    getTravelOrdersSeals,
    saveSeals,
    deleteSeal,
  };
}
